import React, { useEffect, useMemo, useState } from "react";
import Card from "./Card";                   // adjust import if needed
import InputField from "../elements/InputField";
import DropdownField from "../elements/DropdownField";
import Text from "../elements/TextComponent";

export type BotSummary = { botId: string; name: string };
type PositionKind = "bottom-right" | "bottom-left" | "custom";
type ThemeKind = "purple" | "blue" | "green" | "custom";

type Props = {
  bots: BotSummary[];
  /** Base URL hosting /chat/embed; defaults to window.location.origin or http://localhost:4000 */
  defaultPadding?: number;
};

const isHex = (s: string) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s.trim());

const BotEmbedBuilder: React.FC<Props> = ({
  bots,
  defaultPadding = 16,
}) => {
  const computedBase = process.env.REACT_APP_API_URL!;

  // Selection
  const [selectedBotId, setSelectedBotId] = useState<string>(() => bots[0]?.botId || "");
  const selectedBot = useMemo(
    () => bots.find((b) => b.botId === selectedBotId) || bots[0],
    [bots, selectedBotId]
  );

  // Customization
  const [displayName, setDisplayName] = useState<string>(selectedBot?.name || "");
  const [welcome, setWelcome] = useState<string>("Hi!");
  const [themeKind, setThemeKind] = useState<ThemeKind>("purple");
  const [customHex, setCustomHex] = useState<string>("#8b2cd7");
  const [launcher, setLauncher] = useState<boolean>(true);
  const [position, setPosition] = useState<PositionKind>("bottom-right");
  const [width, setWidth] = useState<number>(282);
  const [height, setHeight] = useState<number>(540);

  // Positioning
  const [padding, setPadding] = useState<number>(defaultPadding);
  const [top, setTop] = useState<string>("");
  const [right, setRight] = useState<string>("");
  const [bottom, setBottom] = useState<string>("");
  const [left, setLeft] = useState<string>("");

  const [snippet, setSnippet] = useState<string>("");

  useEffect(() => {
    setDisplayName(selectedBot?.name || "");
  }, [selectedBot?.name]);

  const themeParam = useMemo(() => {
    if (themeKind !== "custom") return themeKind;
    const hex = customHex?.trim().startsWith("#") ? customHex.trim() : `#${customHex.trim()}`;
    return isHex(hex) ? hex : "#8b2cd7";
  }, [themeKind, customHex]);

  const buildSrc = (): string => {
    const u = new URL("/chat/embed", computedBase);
    const params: Record<string, string> = {
      botId: selectedBot?.botId || "",
      name: displayName || (selectedBot?.name || ""),
      theme: themeParam,
      welcome: welcome || "",
      launcher: launcher ? "true" : "none",
      h: String(height),
    };
    Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v));
    return u.toString();
  };

  const buildStyle = (): string => {
    const base = [
      `position:fixed`,
      `width:${width}px`,
      `height:${height}px`,
      `border:0`,
      `z-index:2147483647`,
    ];

    if (position === "bottom-right") {
      base.push(`bottom:${padding}px`, `right:${padding}px`);
    } else if (position === "bottom-left") {
      base.push(`bottom:${padding}px`, `left:${padding}px`);
    } else {
      const edges: string[] = [];
      if (top.trim()) edges.push(`top:${top.trim()}`);
      if (right.trim()) edges.push(`right:${right.trim()}`);
      if (bottom.trim()) edges.push(`bottom:${bottom.trim()}`);
      if (left.trim()) edges.push(`left:${left.trim()}`);
      if (edges.length === 0) edges.push(`bottom:${padding}px`, `right:${padding}px`);
      base.push(...edges);
    }
    return base.join(";");
  };

  const generate = () => {
    if (!selectedBot?.botId) return;
    const src = buildSrc();
    const style = buildStyle();
    const generated = `<iframe
  src="${src}"
  style="${style};"
  scrolling="no"
></iframe>`;
    setSnippet(generated);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = snippet;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  const label: React.CSSProperties = { color: "var(--text)", fontSize: 13, fontWeight: 600 };

  return (
    <Card
      width="92%"
      height="940px"
      style={{
        padding: 16,
        fontFamily: "Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        overflow: "hidden",             // prevent horizontal bleed
      }}
    >
      <h2 style={{ margin: "0 0 16px", fontWeight: 800, color: "var(--text)" }}>
        Generate Embed Iframe
      </h2>
      <Text
          text="This generator will create a html iframe that you can place in your website's html or components to"
          fontSize="16px"
          fontWeight="400"
          lineHeight="28px"
          color="#030303"
          textAlign="left"
      />
       <Text
          text="allow users to interact with your custom assistant via a chat window."
          fontSize="16px"
          fontWeight="400"
          lineHeight="28px"
          color="#030303"
          textAlign="left"
      />
       <Text
          text="It is customizable, notably the luancher field shows a button where users can toggle to opern and close the chat window."
          fontSize="16px"
          fontWeight="400"
          lineHeight="28px"
          color="#030303"
          textAlign="left"
      />
      <Text
          text="When launcher = none the chat window will always appear open."
          fontSize="16px"
          fontWeight="400"
          lineHeight="28px"
          color="#030303"
          textAlign="left"
      />
      <Text
          text="You can customize color, display name, height, position, width, and the welcome message here."
          fontSize="16px"
          fontWeight="400"
          lineHeight="28px"
          color="#030303"
          textAlign="left"
      />
      <Text
          text="When finished click Generate and copy the code and place in your website."
          fontSize="16px"
          fontWeight="400"
          lineHeight="28px"
          color="#030303"
          textAlign="left"
      />
      <div className="margin-top-10"></div>
      {/* Scrollable form area to prefer vertical scrolling */}
      <div
        style={{
          maxHeight: "520px",
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: 4,
        }}
      >
        {/* Bot & Name grid */}
        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", // prevent column expansion
            alignItems: "start",
            marginBottom: 16,
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Bot</span>
            <DropdownField
              value={selectedBotId}
              onChange={(e) => setSelectedBotId(e.target.value)}
              options={bots.map((b) => ({ label: `${b.name} (${b.botId})`, value: b.botId }))}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Name (shown in header)</span>
            <InputField
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              text={selectedBot?.name || "Assistant"}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Welcome Message</span>
            <InputField
              value={welcome}
              onChange={(e) => setWelcome(e.target.value)}
              text="Hi!"
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Theme</span>
            <DropdownField
              value={themeKind}
              onChange={(e) => setThemeKind(e.target.value as ThemeKind)}
              options={[
                { label: "purple", value: "purple" },
                { label: "blue", value: "blue" },
                { label: "green", value: "green" },
                { label: "custom (hex)", value: "custom" },
              ]}
            />
          </label>

          {themeKind === "custom" && (
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={label}>Custom Hex Color</span>
              <InputField
                value={customHex}
                onChange={(e) => setCustomHex(e.target.value)}
                text="#D2042D"
              />
            </label>
          )}

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Launcher</span>
            <DropdownField
              value={launcher ? "true" : "none"}
              onChange={(e) => setLauncher(e.target.value === "true")}
              options={[
                { label: "true (button toggles chat)", value: "true" },
                { label: "none (open by default)", value: "none" },
              ]}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Position</span>
            <DropdownField
              value={position}
              onChange={(e) => setPosition(e.target.value as PositionKind)}
              options={[
                { label: "bottom-right", value: "bottom-right" },
                { label: "bottom-left", value: "bottom-left" },
                { label: "custom", value: "custom" },
              ]}
            />
          </label>

          {position !== "custom" ? (
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={label}>Padding (px)</span>
              <InputField
                value={String(padding)}
                onChange={(e) => setPadding(parseInt(e.target.value || "0", 10))}
                text="16"
              />
            </label>
          ) : (
            // Force the 4 custom fields onto the NEXT ROW and span full width
            <div
              style={{
                gridColumn: "1 / -1",
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                gap: 8,
              }}
            >
              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={label}>top</span>
                <InputField width="auto" value={top} onChange={(e) => setTop(e.target.value)} text="e.g. 16px" />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={label}>right</span>
                <InputField width="auto" value={right} onChange={(e) => setRight(e.target.value)} text="e.g. 16px" />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={label}>bottom</span>
                <InputField width="auto" value={bottom} onChange={(e) => setBottom(e.target.value)} text="e.g. 16px" />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={label}>left</span>
                <InputField width="auto" value={left} onChange={(e) => setLeft(e.target.value)} text="e.g. 16px" />
              </label>
            </div>
          )}
          { position === 'custom' ?
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={label}>Padding (px)</span>
              <InputField
                value={String(padding)}
                onChange={(e) => setPadding(parseInt(e.target.value || "0", 10))}
                text="16"
              />
            </label>
          : null}
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Width (px)</span>
            <InputField
              value={String(width)}
              onChange={(e) => setWidth(parseInt(e.target.value || "0", 10))}
              text="282"
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Height (px)</span>
            <InputField
              value={String(height)}
              onChange={(e) => setHeight(parseInt(e.target.value || "0", 10))}
              text="540"
            />
          </label>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          onClick={generate}
          style={{
            background: "var(--primary)",
            color: "#fff",
            border: 0,
            borderRadius: 12,
            padding: "10px 16px",
            fontWeight: 800,
            cursor: "pointer",
            fontFamily: "Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          }}
        >
          Generate
        </button>

        <button
          onClick={copy}
          disabled={!snippet}
          style={{
            background: snippet ? "var(--primary)" : "#9CA3AF",
            color: "#fff",
            border: 0,
            borderRadius: 12,
            padding: "10px 16px",
            fontWeight: 800,
            cursor: snippet ? "pointer" : "not-allowed",
            opacity: snippet ? 1 : 0.6,
            fontFamily: "Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          }}
        >
          Copy
        </button>
      </div>

      {/* Output */}
      <pre
        style={{
          margin: 0,
          background: "var(--tint, #f9f5ff)",
          color: "var(--text, #111827)",
          borderRadius: 12,
          padding: 12,
          border: "1px solid var(--hover, #7621b9)",
          outline: "2px solid rgba(118, 33, 185, 0.12)",
          overflowX: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: 13,
        }}
      >
        {snippet || "<!-- Your embed snippet will appear here after you click Generate -->"}
      </pre>
    </Card>
  );
};

export default BotEmbedBuilder;
